import React from 'react';
import type { BlankFilter, DateRangeFilter as DateRangeFilterValue } from '@app-types';
import { formatCalendarDate, formatCalendarDateWithoutYear } from '@utils/dateUtils';
import RangeFilter from './RangeFilter';

export interface DateRangeFilterProps {
  columnLabel: string;
  value?: DateRangeFilterValue | BlankFilter;
  onChange: (value: DateRangeFilterValue | BlankFilter | null) => void;
  /** Offer "no date" too, under this label; absent on a required date column. */
  emptyFilterLabel?: string;
}

/** Same wording the date cells in this column use, so the two read as one thing. */
const day = (value: string) => formatCalendarDate(value, value);

/**
 * How a set range reads on the closed trigger.
 *
 * Within one year the year is stated once — "1/10 – 12/31/2025" rather than
 * repeating it — because the trigger is a column-width control and the repeated
 * year is the first thing that gets cut off.
 */
function summariseDates(from: string, to: string): string {
  if (from && to) {
    const sameYear = from.slice(0, 4) === to.slice(0, 4);
    return `${sameYear ? formatCalendarDateWithoutYear(from, from) : day(from)} – ${day(to)}`;
  }
  if (from) return `From ${day(from)}`;
  if (to) return `Until ${day(to)}`;
  return '';
}

/**
 * An inclusive date range for a date column.
 *
 * An end before the start is refused with a message (FR-015 / US2.4) rather than
 * submitted to return a confusingly empty list. Everything else — the panel, the
 * draft, Apply — belongs to {@link RangeFilter}, which the amount filter shares.
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ columnLabel, value, onChange, emptyFilterLabel }) => (
  <RangeFilter
    columnLabel={columnLabel}
    inputType="date"
    emptyLabel="Any date"
    value={value?.kind === 'dateRange' ? value : undefined}
    emptyFilter={
      emptyFilterLabel
        ? { label: emptyFilterLabel, applied: value?.kind === 'blank', onApply: () => onChange({ kind: 'blank' }) }
        : undefined
    }
    onChange={(bounds) => onChange(bounds && { kind: 'dateRange', ...bounds })}
    validate={(from, to) =>
      from && to && to < from ? 'End date must not be before the start date' : null
    }
    summarise={summariseDates}
  />
);

export default DateRangeFilter;
